import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  Documentation,
  DocumentationValidationStatusEnum,
  useEdmDocumentationBulkDestroyMutation,
  useEdmDocumentationBulkUpdateValidationStatusMutation,
  useEdmDocumentationUpdateMutation,
} from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import { GetUrlParams } from '@lib/enums/urls';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import useSearchParam from '@lib/hooks/useSearchParam';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import onDestroyListItemCallBack from '@lib/utils/onDestroyListItemCallBack';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import tableItemsBulkAction from '@lib/utils/tableItemsBulkAction';
import getAllTableItemsIds from '@lib/utils/getAllTableItemsIds';
import useEdmDocumentationsQueryHook from 'utils/fetch/hooks/useEdmDocumentationsQueryHook';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { APP_URLS } from 'constants/urls';
import getDocumentsTableColumns from 'routes/Documentation/DocumentList/utils/getDocumentsTableColumns';
import getTableActionsMenu from 'routes/Documentation/DocumentList/utils/getTableActionsMenu';
import getTableActionsForSelected from 'routes/Documentation/DocumentList/utils/getTableActionsForSelected';
import AssetDocumentations from 'components/AssetDocumentations/AssetDocumentations';
import { UseActionsResult } from './utils/useActions';

type Props = {
  id: string;
  useBulkSelectResult: UseBulkSelectResult<Documentation>;
  useActionsResult: UseActionsResult;
  isViewOnly: boolean;
};

export default function AssetDocumentationsContainer(props: Props) {
  const { id, useBulkSelectResult, useActionsResult, isViewOnly } = props;

  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { getUserAccessByCompany }: ContextProps = useOutletContext();
  // Asset docs are allowed on the free plan, but premium Documentation
  // fields/filters (category, validation, renewal) are BE-gated — pass
  // `isViewLocked` to the docs query + table so they degrade gracefully.
  const isViewLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const [searchQuery] = useSearchParam();
  const [paginationParams] = usePaginationParams();

  const { assetQueryHook } = useActionsResult;

  // Query
  const documentationsQueryHookResult = useEdmDocumentationsQueryHook({
    isViewLocked,
    // `category` sub-resolver is gated by `documentation_management` —
    // requesting it on a plan-locked company returns UPGRADE_PLAN_REQUIRED.
    skipCategory: isViewLocked,
    linkedAssetsId: id,
    paginationParams,
    searchQuery,
  });

  const { collection, firstLoading, loading, totalCount, totalPages } =
    documentationsQueryHookResult;

  // Favorites (column cell callback — no favorites filter on this page)
  const onFavoriteCompleted = useCallback(() => {
    // No favorites mode on asset tab, just let cache.modify handle it
  }, []);

  // Validation status
  const [bulkUpdateValidationStatus] =
    useEdmDocumentationBulkUpdateValidationStatusMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  const onValidationStatusChange = useCallback(
    (docId: string, newStatus: DocumentationValidationStatusEnum) => {
      bulkUpdateValidationStatus({
        variables: {
          ids: [docId],
          attributes: { validationStatus: newStatus },
        },
        onCompleted: () => {
          toast({ content: t('update-document-success') });
          documentationsQueryHookResult.refetch();
        },
      });
    },
    [bulkUpdateValidationStatus, documentationsQueryHookResult, t],
  );

  const onStatusBulkChangeHandler = useCallback(
    (newStatus: DocumentationValidationStatusEnum) => {
      tableItemsBulkAction<Documentation>({
        action: (ids) => {
          bulkUpdateValidationStatus({
            variables: {
              ids,
              attributes: { validationStatus: newStatus },
            },
            onCompleted: () => {
              toast({ content: t('update-document-success') });
              useBulkSelectResult.handleUnselectAllTableItems();
              documentationsQueryHookResult.refetch();
            },
          });
        },
        onGetAllIds: () =>
          getAllTableItemsIds<Documentation>({
            fetchIds: documentationsQueryHookResult.fetchIds,
            useBulkSelectResult,
          }),
        useBulkSelectResult,
      });
    },
    [
      bulkUpdateValidationStatus,
      documentationsQueryHookResult,
      t,
      useBulkSelectResult,
    ],
  );

  // Navigation
  const onOpen = useCallback(
    (item: Documentation) => {
      navigate(
        APP_URLS.dashboard.documentation.view.getDynamicPath({
          pathParts: { id: item.id },
        }),
      );
    },
    [navigate],
  );

  // Delete
  const [deleteIds, setDeleteIds] = useState<string[]>([]);
  const isDeleteModalOpen = deleteIds.length > 0;

  const [destroyMutation, { loading: deleteLoading }] =
    useEdmDocumentationBulkDestroyMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  const onDelete = useCallback((item: Documentation) => {
    setDeleteIds([item.id]);
  }, []);

  const onBulkDelete = useCallback(() => {
    tableItemsBulkAction<Documentation>({
      action: (ids) => setDeleteIds(ids),
      onGetAllIds: () =>
        getAllTableItemsIds<Documentation>({
          fetchIds: documentationsQueryHookResult.fetchIds,
          useBulkSelectResult,
        }),
      useBulkSelectResult,
    });
  }, [documentationsQueryHookResult.fetchIds, useBulkSelectResult]);

  const closeDeleteModal = useCallback(() => {
    setDeleteIds([]);
  }, []);

  const onConfirmedDelete = useCallback(() => {
    if (!deleteIds.length) return;
    const idsToDelete = [...deleteIds];
    destroyMutation({
      variables: { ids: idsToDelete },
      onCompleted: () => {
        setDeleteIds([]);
        useBulkSelectResult.handleUnselectAllTableItems();
        toast({ content: t('destroy-documents-success') });
        onDestroyListItemCallBack({
          collection,
          collectionForDestroy: idsToDelete,
          navigate,
          pathname,
          refetch: () => {
            assetQueryHook.refetch();
            documentationsQueryHookResult.refetch();
          },
          search,
          totalPages,
        });
      },
    });
  }, [
    assetQueryHook,
    collection,
    deleteIds,
    destroyMutation,
    documentationsQueryHookResult,
    navigate,
    pathname,
    search,
    t,
    totalPages,
    useBulkSelectResult,
  ]);

  // Unlink
  const [unlinkDoc, setUnlinkDoc] = useState<Documentation | null>(null);

  const [updateMutation, { loading: unlinkLoading }] =
    useEdmDocumentationUpdateMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  const onUnlink = useCallback((item: Documentation) => {
    setUnlinkDoc(item);
  }, []);

  const closeUnlinkModal = useCallback(() => {
    setUnlinkDoc(null);
  }, []);

  const onConfirmedUnlink = useCallback(() => {
    if (!unlinkDoc) return;
    updateMutation({
      variables: {
        id: unlinkDoc.id,
        attributes: {
          assetLinks: [{ id, unlink: true }],
        },
      },
      onCompleted: () => {
        setUnlinkDoc(null);
        toast({ content: t('unlink-from-equipment-success') });
        assetQueryHook.refetch();
        documentationsQueryHookResult.refetch();
      },
    });
  }, [
    assetQueryHook,
    documentationsQueryHookResult,
    id,
    t,
    unlinkDoc,
    updateMutation,
  ]);

  // File viewer & version notes
  const [fileViewerDocument, setFileViewerDocument] =
    useState<Documentation | null>(null);
  const [versionNotesDocument, setVersionNotesDocument] =
    useState<Documentation | null>(null);

  const onViewDocument = useCallback((item: Documentation) => {
    setFileViewerDocument(item);
  }, []);

  const closeFileViewer = useCallback(() => {
    setFileViewerDocument(null);
  }, []);

  const onVersionNotes = useCallback((item: Documentation) => {
    setVersionNotesDocument(item);
  }, []);

  const closeVersionNotes = useCallback(() => {
    setVersionNotesDocument(null);
  }, []);

  // Table config
  const tableColumns = useMemo(
    () =>
      getDocumentsTableColumns({
        isViewLocked,
        onFavoriteCompleted,
        onOpen,
        onValidationStatusChange,
        t,
      }),
    [isViewLocked, onFavoriteCompleted, onOpen, onValidationStatusChange, t],
  );

  const tableActionsMenu = useMemo(
    () =>
      getTableActionsMenu({
        onViewDocument,
        onVersionNotes,
        onDetails: (item) =>
          navigate(
            APP_URLS.dashboard.documentation.view.getDynamicPath({
              pathParts: { id: item.id },
            }),
          ),
        onEdit: isViewOnly
          ? undefined
          : (item) =>
              navigate(
                APP_URLS.dashboard.documentation.edit.getDynamicPath({
                  pathParts: { id: item.id },
                }),
              ),
        onUnlink: isViewOnly ? undefined : onUnlink,
        onDelete: isViewOnly ? undefined : onDelete,
        t,
      }),
    [
      isViewOnly,
      navigate,
      onDelete,
      onUnlink,
      onVersionNotes,
      onViewDocument,
      t,
    ],
  );

  // Bulk edit
  const [bulkEditIds, setBulkEditIds] = useState<string[]>([]);
  const isBulkEditModalOpen = bulkEditIds.length > 0;

  const onBulkEdit = useCallback(() => {
    tableItemsBulkAction<Documentation>({
      action: (ids) => setBulkEditIds(ids),
      onGetAllIds: () =>
        getAllTableItemsIds<Documentation>({
          fetchIds: documentationsQueryHookResult.fetchIds,
          useBulkSelectResult,
        }),
      useBulkSelectResult,
    });
  }, [documentationsQueryHookResult.fetchIds, useBulkSelectResult]);

  const closeBulkEditModal = useCallback(() => {
    setBulkEditIds([]);
  }, []);

  const onBulkEditCompleted = useCallback(() => {
    closeBulkEditModal();
    documentationsQueryHookResult.refetch();
  }, [closeBulkEditModal, documentationsQueryHookResult]);

  const tableActionsForSelected = useMemo(
    () =>
      getTableActionsForSelected({
        isViewLocked,
        onBulkEdit,
        onDelete: onBulkDelete,
        onStatusBulkChangeHandler,
        selectedItemsCount:
          useBulkSelectResult?.getSelectedItemsCount(totalCount),
        t,
      }),
    [
      isViewLocked,
      onBulkDelete,
      onBulkEdit,
      onStatusBulkChangeHandler,
      totalCount,
      useBulkSelectResult,
      t,
    ],
  );

  const onCreateDocument = useCallback(() => {
    navigate(
      APP_URLS.dashboard.documentation.add.getPathWithQuery({
        [GetUrlParams.AssetId]: id,
        [GetUrlParams.AssetName]: assetQueryHook.data?.name,
      }),
    );
  }, [assetQueryHook.data?.name, id, navigate]);

  return (
    <AssetDocumentations
      assetName={assetQueryHook.data?.name || ''}
      bulkEditIds={bulkEditIds}
      isViewLocked={isViewLocked}
      onBulkEditCompleted={onBulkEditCompleted}
      closeDeleteModal={closeDeleteModal}
      closeFileViewer={closeFileViewer}
      closeUnlinkModal={closeUnlinkModal}
      closeVersionNotes={closeVersionNotes}
      closeBulkEditModal={closeBulkEditModal}
      collection={collection}
      deleteLoading={deleteLoading}
      onCreateDocument={onCreateDocument}
      error={documentationsQueryHookResult.error}
      fileViewerDocument={fileViewerDocument}
      firstLoading={firstLoading}
      isBulkEditModalOpen={isBulkEditModalOpen}
      isDeleteModalOpen={isDeleteModalOpen}
      isUnlinkModalOpen={!!unlinkDoc}
      isViewOnly={isViewOnly}
      loading={loading}
      onConfirmedDelete={onConfirmedDelete}
      onConfirmedUnlink={onConfirmedUnlink}
      tableActionsForSelected={tableActionsForSelected}
      tableActionsMenu={tableActionsMenu}
      tableColumns={tableColumns}
      totalCount={totalCount}
      unlinkLoading={unlinkLoading}
      useBulkSelectResult={useBulkSelectResult}
      versionNotesDocument={versionNotesDocument}
    />
  );
}
